import { Bell, Check, CheckCheck, Trash2 } from '@/ui/icons'
import { useBackendNotificationsRealtime } from '@/shared/hooks/useNotificationsRealtime'
import { useConfirm } from '@/shared/hooks/useConfirm'
import { Button } from '../primitives/button'
import { Badge } from '../primitives/badge'
import { ConfirmationDialog } from '../ConfirmationDialog'
import { formatDistanceToNow } from 'date-fns'
import { cn } from '@/shared/utils'

function normalizeActionUrl(actionUrl: string | undefined): string | undefined {
  if (!actionUrl) return actionUrl

  const mealsMatch = actionUrl.match(/^\/trips\/([^/]+)\/meals$/)
  if (mealsMatch) return `/trips/${mealsMatch[1]}?tab=meals`

  const gearMatch = actionUrl.match(/^\/trips\/([^/]+)\/gear$/)
  if (gearMatch) return `/trips/${gearMatch[1]}?tab=gear`

  const membersMatch = actionUrl.match(/^\/trips\/([^/]+)\/members$/)
  if (membersMatch) return `/trips/${membersMatch[1]}?tab=crew`

  return actionUrl
}

function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'urgent': return 'bg-red-500 text-white'
    case 'high': return 'bg-destructive text-destructive-foreground'
    case 'medium': return 'bg-adventure-600 text-white'
    case 'low': return 'bg-muted text-foreground'
    default: return 'bg-muted text-foreground'
  }
}

export interface NotificationsListContentProps {
  onActionClick?: () => void
}

export function NotificationsListContent({ onActionClick }: NotificationsListContentProps) {
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications
  } = useBackendNotificationsRealtime({ limit: 20 })

  const { confirm, confirmState, close, setLoading } = useConfirm()

  const handleDeleteAll = async () => {
    if (notifications.length === 0) return

    await confirm(
      {
        title: 'Delete All Notifications',
        description: 'Are you sure you want to delete all notifications? This action cannot be undone.',
        variant: 'destructive',
        confirmText: 'Delete All'
      },
      async () => {
        setLoading(true)
        try {
          await deleteAllNotifications()
        } catch (error) {
          throw error
        } finally {
          setLoading(false)
        }
      }
    )
  }

  return (
    <>
      <div className="flex flex-col flex-1 min-h-0 text-foreground">
        {/* Bulk actions */}
        <div className="p-4 border-b border-earth-700 space-y-2 flex-shrink-0">
          {unreadCount > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => markAllAsRead()}
              className="w-full"
            >
              <CheckCheck className="h-4 w-4 mr-2" />
              Mark All as Read ({unreadCount})
            </Button>
          )}
          {notifications.length > 0 && (
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDeleteAll}
              className="w-full"
              disabled={confirmState?.isLoading}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {confirmState?.isLoading ? 'Deleting…' : 'Delete All Notifications'}
            </Button>
          )}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="py-12 text-center text-foreground text-sm">
              Loading...
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-12 text-center">
              <Bell className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-20" />
              <p className="text-foreground">No notifications</p>
              <p className="text-sm text-muted-foreground mt-1">You're all caught up!</p>
            </div>
          ) : (
            <div className="divide-y divide-earth-700">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className="py-4 first:pt-0 last:pb-0 transition-colors bg-earth-50"
                >
                  <div className="flex gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge
                          className={cn(
                            getPriorityColor(notification.priority),
                            'text-[10px] px-1.5 py-0'
                          )}
                        >
                          {notification.priority.toUpperCase()}
                        </Badge>
                        {notification.status === 'unread' && (
                          <Badge variant="default" className="text-[10px] px-1.5 py-0">
                            NEW
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(notification.createdAt), {
                            addSuffix: true
                          })}
                        </span>
                      </div>
                      <h4 className="font-semibold text-sm mb-1 line-clamp-2 text-foreground">
                        {notification.title}
                      </h4>
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {notification.message}
                      </p>
                      {notification.actionUrl && notification.actionText && (
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 h-auto mt-2 text-sm"
                          onClick={() => {
                            const normalizedUrl = normalizeActionUrl(notification.actionUrl)
                            window.location.href = normalizedUrl || notification.actionUrl!
                            onActionClick?.()
                          }}
                        >
                          {notification.actionText} →
                        </Button>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {notification.status === 'unread' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => markAsRead(notification.id)}
                          className="h-8 w-8 p-0"
                          title="Mark as read"
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteNotification(notification.id)}
                        className="h-8 w-8 p-0"
                        title="Delete"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {notifications.length > 0 && (
          <div className="px-4 py-3 border-t border-earth-700 bg-earth-50 flex-shrink-0">
            <p className="text-xs text-center text-muted-foreground">
              Showing {notifications.length} most recent notifications
            </p>
          </div>
        )}
      </div>

      <ConfirmationDialog
        open={confirmState?.open ?? false}
        onOpenChange={(open) => !open && close()}
        title={confirmState?.title}
        description={confirmState?.description ?? ''}
        confirmText={confirmState?.confirmText}
        cancelText={confirmState?.cancelText}
        variant={confirmState?.variant}
        onConfirm={confirmState?.onConfirm ?? (() => {})}
        isLoading={confirmState?.isLoading}
      />
    </>
  )
}
